// Phase 35e — Year-End Reports section embedded in InvestorPortal.
//
// Shows the last 3 years as download cards. Each card hits
// /api/reports/year-end/export.pdf?year=YYYY which returns the branded PDF
// blob; we trigger a browser download via URL.createObjectURL.
//
// Soft-degrades when the user has no deals — the backend still returns a
// valid 6-page PDF with a kind invitation-to-start narrative.

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileDown, Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function _currentYear() {
  return new Date().getFullYear();
}

export default function YearEndReportsSection() {
  const [downloading, setDownloading] = useState(null); // year being downloaded
  const years = [_currentYear(), _currentYear() - 1, _currentYear() - 2];

  const handleDownload = async (year) => {
    setDownloading(year);
    try {
      const resp = await api.get(
        `/reports/year-end/export.pdf?year=${year}`,
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `RunIdea_YearEnd_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Year-end ${year} report downloaded.`);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Could not generate the ${year} report.`,
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <section data-testid="investor-yearend-section">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">
          📊 Year-End Reports
        </h2>
        <span className="text-xs text-slate-500">
          LLM-composed annual narrative + branded PDF
        </span>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {years.map((year) => (
          <div
            key={year}
            className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md"
            data-testid={`investor-yearend-${year}`}
          >
            <div className="flex items-center gap-2">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                <Calendar className="h-4 w-4" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{year}</p>
                <p className="text-[11px] text-slate-500">Year-end report</p>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleDownload(year)}
              disabled={downloading === year}
              data-testid={`investor-yearend-download-${year}`}
              className="w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            >
              {downloading === year ? (
                <>
                  <Loader2 className="me-1.5 h-3.5 w-3.5 animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <FileDown className="me-1.5 h-3.5 w-3.5" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
